import { ChevronDown, ChevronUp } from "lucide-react";
import { useGetList, useTranslate } from "ra-core";
import { useState } from "react";
import { Link } from "react-router";
import { cn } from "@/lib/utils";

import { fillScript, scriptForStage, type Script } from "./types";

/**
 * The script beside a call: steps to tick off and quick answers to objections.
 * The client's own objections (buyer profile) come first and stand out.
 */
export const LeadScript = ({
  stage,
  firstName,
  clientObjections = [],
}: {
  stage: string | null | undefined;
  firstName?: string | null;
  clientObjections?: string[];
}) => {
  const translate = useTranslate();
  const [open, setOpen] = useState(true);
  const [done, setDone] = useState<number[]>([]);
  const [answer, setAnswer] = useState<string | null>(null);
  const { data: scripts = [] } = useGetList<Script>("scripts", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "id", order: "ASC" },
  });
  const script = scriptForStage(scripts, stage);
  if (!script) return null;
  const objections = [...script.objections].sort(
    (a, b) =>
      Number(clientObjections.includes(b.key)) -
      Number(clientObjections.includes(a.key)),
  );
  const shown = objections.find((o) => o.key === answer);

  return (
    <section className="min-w-0 rounded-2xl border bg-card p-4 sm:p-5">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between gap-2 text-left"
      >
        <div>
          <h2 className="text-lg font-bold">
            {translate("crm.scripts.on_call")}
          </h2>
          <div className="text-xs text-muted-foreground">
            {script.title} · {done.length}/{script.steps.length}
          </div>
        </div>
        {open ? (
          <ChevronUp className="size-5" />
        ) : (
          <ChevronDown className="size-5" />
        )}
      </button>
      {open ? (
        <div className="mt-4 flex flex-col gap-4">
          <ol className="flex flex-col gap-2">
            {script.steps.map((step, index) => {
              const ticked = done.includes(index);
              return (
                <li key={index}>
                  <button
                    type="button"
                    onClick={() =>
                      setDone(
                        ticked
                          ? done.filter((i) => i !== index)
                          : [...done, index],
                      )
                    }
                    className={cn(
                      "flex w-full gap-3 rounded-xl border p-3 text-left transition-colors",
                      ticked
                        ? "border-[#8fc5a3] bg-[#e3f1e8]/70 dark:bg-[#1c3a29]/60"
                        : "bg-card hover:bg-muted/40",
                    )}
                  >
                    <span
                      className={cn(
                        "flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-bold",
                        ticked
                          ? "bg-[#2f7a50] text-white"
                          : "bg-muted text-muted-foreground",
                      )}
                    >
                      {ticked ? "✓" : index + 1}
                    </span>
                    <span className="min-w-0">
                      <span
                        className={cn(
                          "block font-semibold",
                          ticked && "text-muted-foreground",
                        )}
                      >
                        {step.title}
                      </span>
                      {!ticked ? (
                        <span className="block text-sm text-muted-foreground">
                          {fillScript(step.text, firstName)}
                        </span>
                      ) : null}
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
          {objections.length ? (
            <div className="flex flex-col gap-2">
              <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                {translate("crm.scripts.if_client_says")}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {objections.map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => setAnswer(answer === o.key ? null : o.key)}
                    className={cn(
                      "rounded-full border px-3 py-1 text-sm font-semibold",
                      answer === o.key
                        ? "border-primary bg-primary text-primary-foreground"
                        : clientObjections.includes(o.key)
                          ? "border-[#d99b92] bg-[#f3dcd8] text-[#a82f23]"
                          : "bg-card text-muted-foreground",
                    )}
                  >
                    {o.title}
                  </button>
                ))}
              </div>
              {shown ? (
                <p className="rounded-xl bg-accent/60 p-3 text-sm">
                  {fillScript(shown.answer, firstName)}
                </p>
              ) : null}
            </div>
          ) : null}
          <Link
            to="/scripts"
            className="self-end text-xs text-muted-foreground underline"
          >
            {translate("crm.scripts.all")}
          </Link>
        </div>
      ) : null}
    </section>
  );
};
